import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Home from "./components/Home";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import { ClipLoader } from "react-spinners";

// Create a simple Settings Context to pass settings to child components
export const SettingsContext = React.createContext(null);

// Custom hook to use settings in any component
export const useSettings = () => {
  const context = React.useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within SettingsContext.Provider");
  }
  // The context value (settingsData) is the single, flattened object.
  return context;
};

function App() {
  const [authChecking, setAuthChecking] = useState(true);
  // settingsData will now store the single, flattened settings object
  const [settingsData, setSettingsData] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [BackendURL, setBackendURL] = useState(
    process.env.REACT_APP_LARAVEL_URL
  );
  const pathName = window.location.pathname;

  useEffect(() => {
    const initializeApp = async () => {
      // Skip auth check for login and register pages
      if (pathName === "/login" || pathName === "/register") {
        setAuthChecking(false);
        setSettingsLoading(false);
        return;
      }

      const storedToken = window.localStorage.getItem("laravel-token");

      if (!storedToken) {
        window.location.href = "/login";
        return;
      }

      try {
        // 1. First, verify authentication
        const login_url = BackendURL + "me";
        const authResponse = await fetch(login_url, {
          method: "GET",
          mode: "cors",
          headers: {
            Accept: "application/json",
            "access-control-allow-origin": "*",
            Authorization: "Bearer " + storedToken,
          },
        });

        const authData = await authResponse.json();

        if (
          authData.status === false ||
          authData.status === undefined ||
          authData.message === "Unauthenticated"
        ) {
          window.localStorage.removeItem("laravel-token");
          window.location.href = "/login";
          return;
        }

        // Always refresh permissions from the server on every page load.
        // This prevents a blank sidebar when old stale values are cached in localStorage.
        if (authData.permission) {
          window.localStorage.setItem("user-permissions", authData.permission);
          window.localStorage.setItem("user-type", authData.user?.type || "");
        }

        setAuthChecking(false);

        // 2. After auth is verified, load settings
        const settingsResponse = await fetch(BackendURL + "setting-listing", {
          method: "GET",
          mode: "cors",
          headers: {
            Accept: "application/json",
            "access-control-allow-origin": "*",
            Authorization: "Bearer " + storedToken,
          },
        });
        const settingsResult = await settingsResponse.json();

        if (settingsResult.data) {
          const rawSettingsArray = settingsResult.data;

          // ⭐ CRITICAL FIX: Flatten the array of objects into a single object
          const flattenedSettings = rawSettingsArray.reduce(
            (acc, currentItem) => {
              // Check for non-empty ID before flattening
              if (currentItem.id !== "") {
                // Combine the single key-value pair into the accumulator object
                return {
                  ...acc,
                  [currentItem.name]: currentItem.value,
                };
              }
              return acc;
            },
            {}
          ); // Start with an empty object {}
          console.log("Flattened Settings:", flattenedSettings?.BACKEND_URL); // Debug log
          if (flattenedSettings?.BACKEND_URL !== undefined) {
            let backend_url_val = flattenedSettings?.BACKEND_URL;
            if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
              backend_url_val = BackendURL;
              flattenedSettings.BACKEND_URL = BackendURL;
            }
            setBackendURL(backend_url_val || BackendURL);
            setSettingsData(flattenedSettings);

            // Cache the raw settings data (optional: you might prefer caching the flattened version)
            localStorage.setItem(
              "app-settings",
              JSON.stringify(rawSettingsArray)
            );
          }
        }
      } catch (error) {
        console.error("Initialization error:", error);
        // Try to load cached settings if API fails
        const cachedSettings = localStorage.getItem("app-settings");
        if (cachedSettings) {
          // Note: If loading from cache, it must also be flattened if it wasn't already stored flattened.
          const rawSettingsArray = JSON.parse(cachedSettings);
          const flattenedSettings = rawSettingsArray.reduce(
            (acc, currentItem) => {
              return {
                ...acc,
                [currentItem.name]: currentItem.value,
              };
            },
            {}
          );
          if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
            flattenedSettings.BACKEND_URL = BackendURL;
          }
          setSettingsData(flattenedSettings);
        }
        setAuthChecking(false);
      } finally {
        setSettingsLoading(false);
      }
    };

    initializeApp();
  }, [pathName]);

  // Show loading spinner while checking authentication or loading settings
  if (authChecking || settingsLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <ClipLoader color={"#123abc"} size={50} />
        <p style={{ marginTop: "20px" }}>
          {authChecking ? "Authenticating..." : "Loading settings..."}
        </p>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={settingsData}>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/*" element={<Home />} />
        </Routes>
      </Router>
    </SettingsContext.Provider>
  );
}

export default App;
